/**
 * Centralized SEO Configuration
 * All SEO-related data, metadata, and schema configurations
 */

import { SITE_PRICE_RANGE } from './pricing';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface SiteConfig {
  name: string;
  tagline: string;
  url: string;
  /**
   * E.164-friendly form used for `tel:` href links and machine-readable
   * fields (Schema.org `telephone`, GA `phone_number`, etc.). Never display
   * this directly to users — use `phoneDisplay` instead.
   */
  phone: string;
  /**
   * Human-readable form. MUST match Google Business Profile exactly,
   * character-for-character, including parentheses and spacing. Local SEO
   * algorithms compare visible NAP strings across the web; mismatched
   * formats reduce trust signals.
   */
  phoneDisplay: string;
  email: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  geo: {
    latitude: number;
    longitude: number;
  };
  social: {
    facebook?: string;
    instagram?: string;
    yelp?: string;
    twitter?: string;
  };
  openingHours: string[];
  openingHoursDisplay: string;
  priceRange: string;
}

export interface PageMeta {
  title: string;
  description: string;
  canonical: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface ServiceData {
  id: string;
  name: string;
  slug: string;
  shortDescription: string;
  longDescription: string;
  price: string;
  priceValue: number;
  features: string[];
  petTypes: ('dog' | 'cat')[];
  keywords: string[];
}

export interface LocationData {
  id: string;
  name: string;
  slug: string;
  county: string;
  state: string;
  landmark: string;
  driveTime: string;
  neighborhoods: string[];
  zipCodes: string[];
  uniqueContent: string;
  petStats: string;
  nearbyLocations: string[];
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  metaDescription: string;
  date: string;
  datePublished: string; // ISO format
  dateModified: string; // ISO format
  readTime: string;
  category: string;
  tags: string[];
  image?: string;
  imageAlt?: string;
  author: string;
  featured?: boolean;
}

export interface FAQItem {
  question: string;
  answer: string;
  category?: string;
}

// ============================================================================
// SITE CONFIGURATION
// ============================================================================

export const siteConfig: SiteConfig = {
  name: 'CHOMP Pet Dental',
  tagline: 'The Future of Pet Dental Care',
  url: 'https://chomp.vet',
  // CANONICAL NAP (Name / Address / Phone). Must match Google Business
  // Profile EXACTLY character-for-character. If GBP is updated, update here
  // and in public/index.html (og + JSON-LD address blocks) at the same time.
  // GBP currently displays: (954) 466-3775
  phone: '+1-954-466-3775',
  phoneDisplay: '(954) 466-3775',
  email: 'info@chomp.vet',
  address: {
    street: '1800 E Commercial Blvd',
    city: 'Fort Lauderdale',
    state: 'FL',
    zip: '33308',
    country: 'US',
  },
  geo: {
    latitude: 26.1224,
    longitude: -80.1373,
  },
  social: {
    facebook: 'https://www.facebook.com/chomppetdental',
    instagram: 'https://www.instagram.com/chomp_petdental',
    yelp: 'https://www.yelp.com/biz/chomp-pet-dental-fort-lauderdale',
    twitter: 'https://x.com/chomppetdental',
  },
  // Confirmed by the clinic owner on September 9, 2026.
  openingHours: ['Mo-Fr 08:00-17:00'],
  openingHoursDisplay: 'Monday–Friday, 8 a.m.–5 p.m.',
  priceRange: SITE_PRICE_RANGE,
};

// ============================================================================
// SERVICES DATA
// ============================================================================

export const services: ServiceData[] = [
  {
    id: 'routine-dental',
    name: 'Routine Dental Cleaning',
    slug: 'routine-dental-cleaning',
    shortDescription: 'Professional teeth cleaning for healthy pets',
    longDescription:
      'Full dental cleaning under anesthesia with X-rays, scaling, charting, and polishing. Published rates: $495 (cat) / $595 (dog) for the cleaning procedure; pre-anesthetic bloodwork ($150) is itemized separately, and the doctor exam is complimentary when you proceed with CHOMP-recommended care ($50 if you do not proceed).',
    price: '$495 Cat / $595 Dog',
    priceValue: 495,
    features: [
      'Veterinary exam (complimentary when proceeding; $50 otherwise)',
      'IV catheter & fluids',
      'Sedation & anesthesia',
      'Digital dental X-rays',
      'Ultrasonic scaling & polishing',
      'Fluoride treatment',
      'Aftercare instructions; medications billed separately if required',
      'AI-powered dental health report',
      'Free recheck 2-3 weeks later',
    ],
    petTypes: ['dog', 'cat'],
    keywords: ['pet teeth cleaning', 'dog dental cleaning', 'cat teeth cleaning', 'routine dental', 'pet dental exam'],
  },
  {
    id: 'mild-moderate-dental',
    name: 'Mild to Moderate Dental Treatment',
    slug: 'moderate-dental-treatment',
    shortDescription: 'Full dental with extractions as needed',
    longDescription:
      'Cleaning plus extractions and adjunctive care at published per-tooth rates ($100–$200 by tooth type). Typical bundled visits often start around $745+ depending on bloodwork, cleaning, and Periovive; your written estimate lists every line item.',
    price: 'From $745+',
    priceValue: 745,
    features: [
      'Everything in Routine Dental',
      'Extractions priced separately by tooth type',
      'Periodontal treatment',
      'Advanced pain management',
      'Extended monitoring',
    ],
    petTypes: ['dog', 'cat'],
    keywords: ['pet tooth extraction', 'dog dental surgery', 'cat dental treatment', 'periodontal disease treatment'],
  },
  {
    id: 'advanced-dental',
    name: 'Advanced Dental Surgery',
    slug: 'advanced-dental-surgery',
    shortDescription: 'Complex dental procedures',
    longDescription: 'Surgery-grade dental care for severe dental disease, including more than 5 extractions and complex oral surgery procedures.',
    price: '$1595+',
    priceValue: 1595,
    features: [
      'Everything in Moderate Dental',
      'Complex extractions (6+)',
      'Oral surgery',
      'Bone grafting if needed',
      'Extended recovery support',
    ],
    petTypes: ['dog', 'cat'],
    keywords: ['pet oral surgery', 'dog dental surgery', 'cat oral surgery', 'complex dental', 'severe dental disease'],
  },
];

// ============================================================================
// LOCATIONS DATA
// ============================================================================

export const locations: LocationData[] = [
  {
    id: 'fort-lauderdale',
    name: 'Fort Lauderdale',
    slug: 'fort-lauderdale',
    county: 'Broward',
    state: 'FL',
    landmark: 'Las Olas Boulevard',
    driveTime: '0 minutes',
    neighborhoods: ['Las Olas', 'Victoria Park', 'Colee Hammock', 'Flagler Village', 'Rio Vista', 'Lauderdale-by-the-Sea', 'Wilton Manors', 'Oakland Park'],
    zipCodes: ['33301', '33304', '33305', '33306', '33308', '33309', '33311', '33312', '33315', '33316'],
    uniqueContent: 'As the home of CHOMP Pet Dental, Fort Lauderdale pet parents enjoy the convenience of South Florida\'s first dedicated veterinary dental clinic right in their backyard. Located just minutes from Las Olas and I-95, we\'re easily accessible from anywhere in the city.',
    petStats: 'Broward County has over 500,000 pet-owning households, and 80% of pets over age 3 have dental disease.',
    nearbyLocations: ['pompano-beach', 'hollywood', 'oakland-park'],
    coordinates: { latitude: 26.1224, longitude: -80.1373 },
  },
  {
    id: 'boca-raton',
    name: 'Boca Raton',
    slug: 'boca-raton',
    county: 'Palm Beach',
    state: 'FL',
    landmark: 'Mizner Park',
    driveTime: '25 minutes',
    neighborhoods: ['Mizner Park', 'Town Center', 'Boca West', 'Broken Sound', 'Royal Palm Yacht Club', 'Spanish River', 'Boca Del Mar', 'Woodfield Country Club'],
    zipCodes: ['33431', '33432', '33433', '33434', '33486', '33487', '33496', '33498'],
    uniqueContent: 'Boca Raton pet parents drive just 25 minutes south on I-95 to reach CHOMP Pet Dental. Our dedicated dental facility offers something no general practice in Palm Beach County can match: veterinarians who focus exclusively on pet dental health, every single day.',
    petStats: 'Palm Beach County pet owners spend 40% more on pet healthcare than the national average.',
    nearbyLocations: ['deerfield-beach', 'delray-beach', 'boynton-beach'],
    coordinates: { latitude: 26.3587, longitude: -80.0831 },
  },
  {
    id: 'miami',
    name: 'Miami',
    slug: 'miami',
    county: 'Miami-Dade',
    state: 'FL',
    landmark: 'Wynwood',
    driveTime: '35 minutes',
    neighborhoods: ['Brickell', 'Downtown', 'Coral Gables', 'Coconut Grove', 'Wynwood', 'South Beach', 'Key Biscayne', 'Miami Beach', 'Aventura'],
    zipCodes: ['33125', '33126', '33127', '33128', '33129', '33130', '33131', '33132', '33133', '33134', '33139', '33140'],
    uniqueContent: 'Miami pet parents trust CHOMP Pet Dental for specialized dental care worth the short drive north. With transparent, flat-rate pricing and a team that does nothing but dental procedures, you\'ll get better results than any general practice in Miami-Dade.',
    petStats: 'Miami-Dade County has the highest pet population in Florida, with over 700,000 dogs and cats.',
    nearbyLocations: ['miami-beach', 'coral-gables', 'aventura'],
    coordinates: { latitude: 25.7617, longitude: -80.1918 },
  },
  {
    id: 'pompano-beach',
    name: 'Pompano Beach',
    slug: 'pompano-beach',
    county: 'Broward',
    state: 'FL',
    landmark: 'Pompano Beach Pier',
    driveTime: '15 minutes',
    neighborhoods: ['Hillsboro Beach', 'Lighthouse Point', 'Coconut Creek', 'Palm Aire', 'Cypress Creek', 'North Andrews Gardens'],
    zipCodes: ['33060', '33061', '33062', '33063', '33064', '33065', '33066', '33069'],
    uniqueContent: 'Pompano Beach residents are just 15 minutes from CHOMP Pet Dental. Visit our Fort Lauderdale clinic for an assessment of your dog or cat’s dental needs.',
    petStats: 'Pompano Beach has seen a 30% increase in pet ownership since 2020.',
    nearbyLocations: ['fort-lauderdale', 'deerfield-beach', 'lighthouse-point'],
    coordinates: { latitude: 26.2379, longitude: -80.1248 },
  },
  {
    id: 'hollywood',
    name: 'Hollywood',
    slug: 'hollywood',
    county: 'Broward',
    state: 'FL',
    landmark: 'Hollywood Broadwalk',
    driveTime: '10 minutes',
    neighborhoods: ['Hollywood Beach', 'Young Circle', 'Emerald Hills', 'Dania Beach', 'Hallandale Beach', 'Pembroke Park'],
    zipCodes: ['33019', '33020', '33021', '33022', '33023', '33024', '33025', '33026'],
    uniqueContent: 'Hollywood pet owners can reach CHOMP Pet Dental in just 10 minutes via I-95. Our specialized dental clinic offers the expertise and focused care that general veterinary practices simply cannot match.',
    petStats: 'Hollywood is one of the most dog-friendly cities in Florida with over 20 dog parks.',
    nearbyLocations: ['fort-lauderdale', 'hallandale-beach', 'dania-beach'],
    coordinates: { latitude: 26.0112, longitude: -80.1495 },
  },
  {
    id: 'coral-springs',
    name: 'Coral Springs',
    slug: 'coral-springs',
    county: 'Broward',
    state: 'FL',
    landmark: 'The Walk of Coral Springs',
    driveTime: '20 minutes',
    neighborhoods: ['Heron Bay', 'Wyndham Lakes', 'Parkland', 'Margate', 'Tamarac', 'Coconut Creek'],
    zipCodes: ['33065', '33071', '33073', '33075', '33076', '33077'],
    uniqueContent: 'Coral Springs families trust CHOMP Pet Dental for their pets\' oral health. Our dedicated dental clinic is just 20 minutes away via the Sawgrass Expressway, offering specialized care at transparent prices.',
    petStats: 'Coral Springs has one of the highest rates of pet insurance adoption in South Florida.',
    nearbyLocations: ['parkland', 'margate', 'coconut-creek'],
    coordinates: { latitude: 26.2712, longitude: -80.2706 },
  },
  {
    id: 'plantation',
    name: 'Plantation',
    slug: 'plantation',
    county: 'Broward',
    state: 'FL',
    landmark: 'Westfield Broward',
    driveTime: '12 minutes',
    neighborhoods: ['Jacaranda', 'Central Park', 'Plantation Acres', 'Sunrise', 'Lauderhill'],
    zipCodes: ['33317', '33322', '33324', '33325', '33388'],
    uniqueContent: 'Plantation pet parents are just 12 minutes from CHOMP Pet Dental. Our practice focuses on dental care for dogs and cats. Ask about an individual assessment and itemized estimate.',
    petStats: 'Plantation ranks among the top 10 cities in Florida for pet wellness spending.',
    nearbyLocations: ['fort-lauderdale', 'sunrise', 'davie'],
    coordinates: { latitude: 26.1276, longitude: -80.2331 },
  },
  {
    id: 'davie',
    name: 'Davie',
    slug: 'davie',
    county: 'Broward',
    state: 'FL',
    landmark: 'Bergeron Rodeo Grounds',
    driveTime: '15 minutes',
    neighborhoods: ['Nova Southeastern', 'Pine Island Ridge', 'Rolling Hills', 'Countryside Ranches'],
    zipCodes: ['33314', '33324', '33325', '33328', '33330', '33331'],
    uniqueContent: 'Davie pet owners, known for their love of horses and pets, appreciate CHOMP Pet Dental\'s dedicated approach to veterinary dentistry. Just 15 minutes away, we provide the specialized care your companions deserve.',
    petStats: 'Davie has one of the highest rates of multi-pet households in South Florida.',
    nearbyLocations: ['plantation', 'southwest-ranches', 'weston'],
    coordinates: { latitude: 26.0765, longitude: -80.2521 },
  },
  {
    id: 'weston',
    name: 'Weston',
    slug: 'weston',
    county: 'Broward',
    state: 'FL',
    landmark: 'Weston Town Center',
    driveTime: '20 minutes',
    neighborhoods: ['Weston Hills', 'The Isles', 'Savanna', 'Bonaventure'],
    zipCodes: ['33326', '33327', '33331', '33332'],
    uniqueContent: 'Weston families choose CHOMP Pet Dental for premium veterinary dental care. Our 20-minute drive from Weston delivers specialized expertise you won\'t find at any general practice.',
    petStats: 'Weston pet owners report the highest satisfaction rates with specialized veterinary care in Broward County.',
    nearbyLocations: ['davie', 'southwest-ranches', 'pembroke-pines'],
    coordinates: { latitude: 26.1004, longitude: -80.3998 },
  },
  {
    id: 'pembroke-pines',
    name: 'Pembroke Pines',
    slug: 'pembroke-pines',
    county: 'Broward',
    state: 'FL',
    landmark: 'Pembroke Lakes Mall',
    driveTime: '18 minutes',
    neighborhoods: ['Chapel Trail', 'Silver Lakes', 'Pembroke Falls', 'Pembroke Shores'],
    zipCodes: ['33023', '33024', '33025', '33026', '33027', '33028', '33029'],
    uniqueContent: 'Pembroke Pines pet parents trust CHOMP Pet Dental for expert veterinary dental care. Just 18 minutes north, our practice focuses on dental care for dogs and cats.',
    petStats: 'Pembroke Pines is Broward County\'s second-largest city with over 100,000 registered pets.',
    nearbyLocations: ['hollywood', 'miramar', 'cooper-city'],
    coordinates: { latitude: 26.0129, longitude: -80.3418 },
  },
  {
    id: 'deerfield-beach',
    name: 'Deerfield Beach',
    slug: 'deerfield-beach',
    county: 'Broward',
    state: 'FL',
    landmark: 'Deerfield Beach Pier',
    driveTime: '20 minutes',
    neighborhoods: ['Cove', 'Hillsboro Pines', 'Deer Creek', 'Crystal Lake'],
    zipCodes: ['33441', '33442', '33443'],
    uniqueContent: 'Deerfield Beach pet owners are just 20 minutes from CHOMP Pet Dental. Our beachside community deserves specialized dental care that keeps pets healthy for years of beach walks.',
    petStats: 'Deerfield Beach has one of the most active dog beach communities in South Florida.',
    nearbyLocations: ['boca-raton', 'pompano-beach', 'lighthouse-point'],
    coordinates: { latitude: 26.3185, longitude: -80.0998 },
  },
  {
    id: 'aventura',
    name: 'Aventura',
    slug: 'aventura',
    county: 'Miami-Dade',
    state: 'FL',
    landmark: 'Aventura Mall',
    driveTime: '25 minutes',
    neighborhoods: ['Aventura', 'Sunny Isles Beach', 'Golden Beach', 'North Miami Beach'],
    zipCodes: ['33160', '33180'],
    uniqueContent: 'Aventura pet parents discover CHOMP Pet Dental is worth the 25-minute drive. Discuss dental cleaning, X-rays, and treatment options with our veterinary team.',
    petStats: 'Aventura has the highest concentration of luxury pet services in Miami-Dade County.',
    nearbyLocations: ['miami', 'hallandale-beach', 'sunny-isles-beach'],
    coordinates: { latitude: 25.9565, longitude: -80.1392 },
  },
];

// ============================================================================
// BLOG POSTS DATA
// ============================================================================

export const blogPosts: BlogPost[] = [
  {
    slug: 'signs-dog-needs-dental-cleaning',
    title: '7 Signs Your Dog Needs a Professional Dental Cleaning',
    excerpt: 'Bad breath is just the beginning. Learn the warning signs that indicate your dog needs professional dental care before serious problems develop.',
    metaDescription: 'Learn the 7 warning signs that your dog needs professional dental cleaning. Bad breath, tartar buildup, and more. Fort Lauderdale veterinary dentist explains.',
    date: 'December 2024',
    datePublished: '2024-12-01',
    dateModified: '2024-12-01',
    readTime: '5 min read',
    category: 'Dog Dental Health',
    tags: ['dog dental health', 'dental cleaning', 'warning signs', 'pet health'],
    image: '/blog/dog-dental-signs.jpg',
    imageAlt: 'Dog showing healthy teeth after professional dental cleaning',
    author: 'CHOMP Pet Dental',
    featured: true,
  },
  {
    slug: 'pet-dental-cleaning-cost-fort-lauderdale',
    title: 'Dog & Cat Dental Cleaning Costs in Fort Lauderdale',
    excerpt: 'CHOMP’s published cleaning prices, separate fees, and what to ask before agreeing to a dental estimate.',
    metaDescription: 'CHOMP cat cleaning is $495 and dog cleaning is $595. Bloodwork is $150 extra. Compare core subtotals, extraction fees, and exam conditions.',
    date: 'September 2026',
    datePublished: '2024-12-01',
    dateModified: '2026-09-09',
    readTime: '4 min read',
    category: 'Cost & Value',
    tags: ['pet dental cost', 'Fort Lauderdale', 'pricing', 'value'],
    image: '/Chomp-Front.png',
    imageAlt: 'CHOMP Pet Dental clinic in Fort Lauderdale',
    author: 'CHOMP Pet Dental',
    featured: true,
  },
  {
    slug: 'is-anesthesia-safe-for-pet-dental-cleaning',
    title: "Anesthesia for Pet Dental Cleaning: Risks and Questions to Ask",
    excerpt: "Why dental care uses anesthesia, what affects individual risk, and what to ask before your dog or cat’s procedure.",
    metaDescription: "Understand anesthesia for dog and cat dental cleaning: assessment, monitoring, preparation, recovery, and questions for your veterinarian.",
    date: 'December 2024',
    datePublished: '2024-12-01',
    dateModified: '2026-09-09',
    readTime: '3 min read',
    category: 'Safety',
    tags: ['anesthesia', 'pet safety', 'dental procedure', 'veterinary care'],
    image: '/Chomp-Front.png',
    imageAlt: 'CHOMP Pet Dental clinic in Fort Lauderdale',
    author: 'CHOMP Pet Dental',
    featured: false,
  },
  {
    slug: 'cat-dental-disease-signs-treatment',
    title: "Cat Dental Disease: Signs, Examination, and Treatment",
    excerpt: "Recognize possible dental problems in cats, understand the examination, and prepare questions about treatment and cost.",
    metaDescription: "Cat dental disease signs, dental X-rays, tooth resorption, treatment questions, and Chomp’s published cat cleaning fees in Fort Lauderdale.",
    date: 'December 2024',
    datePublished: '2024-12-01',
    dateModified: '2026-09-09',
    readTime: '3 min read',
    category: 'Cat Dental Health',
    tags: ['cat dental health', 'dental disease', 'feline dentistry', 'cat care'],
    image: '/Chomp-Front.png',
    imageAlt: 'CHOMP Pet Dental clinic in Fort Lauderdale',
    author: 'CHOMP Pet Dental',
    featured: false,
  },
  {
    slug: 'non-anesthetic-dental-cleaning-truth',
    title: 'The Truth About Non-Anesthetic Dental Cleaning for Pets',
    excerpt: "Non-anesthetic dental cleanings are popular but controversial. Here's what veterinary dentists say about their effectiveness.",
    metaDescription: "The truth about non-anesthetic dental cleaning for pets. Learn why veterinary dentists don't recommend it and what works better.",
    date: 'December 2024',
    datePublished: '2024-12-01',
    dateModified: '2024-12-01',
    readTime: '5 min read',
    category: 'Education',
    tags: ['non-anesthetic dental', 'pet dental myths', 'dental cleaning', 'education'],
    image: '/blog/non-anesthetic.jpg',
    imageAlt: 'Professional veterinary dental equipment',
    author: 'CHOMP Pet Dental',
    featured: false,
  },
  {
    slug: 'how-often-dogs-need-dental-cleaning',
    title: 'How Often Do Dogs Need Dental Cleaning? Age-by-Age Guide',
    excerpt: 'Your dog\'s dental needs change with age. Learn the recommended cleaning schedule from puppy to senior.',
    metaDescription: 'How often should your dog get professional dental cleaning? Age-by-age guide from puppies to seniors. Fort Lauderdale vet advice.',
    date: 'December 2024',
    datePublished: '2024-12-15',
    dateModified: '2024-12-15',
    readTime: '5 min read',
    category: 'Dog Dental Health',
    tags: ['dog dental cleaning', 'cleaning frequency', 'dog age', 'preventive care'],
    image: '/blog/dog-age-dental.jpg',
    imageAlt: 'Dogs of different ages showing healthy smiles',
    author: 'CHOMP Pet Dental',
    featured: false,
  },
  {
    slug: 'dog-bad-breath-causes-solutions',
    title: 'Why Does My Dog Have Bad Breath? Causes and Solutions',
    excerpt: 'Dog bad breath isn\'t normal. Learn what causes it and how professional dental care can eliminate it.',
    metaDescription: 'Why does your dog have bad breath? Learn the common causes and proven solutions from Fort Lauderdale veterinary dental specialists.',
    date: 'December 2024',
    datePublished: '2024-12-18',
    dateModified: '2024-12-18',
    readTime: '4 min read',
    category: 'Dog Dental Health',
    tags: ['dog bad breath', 'halitosis', 'dental disease', 'oral health'],
    image: '/blog/dog-bad-breath.jpg',
    imageAlt: 'Happy dog with fresh breath after dental treatment',
    author: 'CHOMP Pet Dental',
    featured: false,
  },
  {
    slug: 'pet-dental-disease-heart-kidney-connection',
    title: 'How Dental Disease Affects Your Pet\'s Heart, Kidneys, and Liver',
    excerpt: 'Dental disease doesn\'t stay in the mouth. Learn how bacteria spread and damage vital organs.',
    metaDescription: 'How pet dental disease affects heart, kidneys, and liver health. Learn why treating dental disease can extend your pet\'s life.',
    date: 'December 2024',
    datePublished: '2024-12-20',
    dateModified: '2024-12-20',
    readTime: '6 min read',
    category: 'Pet Health',
    tags: ['dental disease', 'organ health', 'heart disease', 'kidney disease'],
    image: '/blog/dental-organ-health.jpg',
    imageAlt: 'Healthy pet receiving veterinary checkup',
    author: 'CHOMP Pet Dental',
    featured: true,
  },
];

// ============================================================================
// FAQ DATA
// ============================================================================

export const faqItems: FAQItem[] = [
  {
    question: 'How often should my pet have a dental cleaning?',
    answer: "Most pets benefit from annual dental cleanings. We'll recommend a schedule based on your pet's needs, age, breed, and current dental health. Some pets may need more frequent cleanings, while others with excellent oral health might go longer between treatments.",
    category: 'General',
  },
  {
    question: 'Why does my pet need anesthesia for a dental cleaning?',
    answer: "Periodontal disease hides under the gumline where bacteria damage the tissues that hold teeth in place. Effective treatment means cleaning and treating those areas, not just the visible tooth surface.\n\nAnesthesia is recommended because it:\n• Allows gentle, thorough cleaning under the gums and between teeth\n• Makes dental x-rays possible to find hidden problems\n• Provides pain control and keeps pets from stress and fear\n• Protects the airway from water and debris during the procedure\n• Lets the veterinarian work precisely, safely, and efficiently",
    category: 'Anesthesia',
  },
  {
    question: 'Is non-anesthetic dental real?',
    answer: "No, non-anesthetic dental cleanings only address the visible portion of the teeth and cannot clean below the gumline where disease actually starts. They can also be stressful for pets and give a false sense of security while disease progresses unseen. True dental disease occurs below the gum line, which can only be properly cleaned and treated under anesthesia.",
    category: 'Education',
  },
  {
    question: 'What are signs of dental problems in pets?',
    answer: 'Common signs include bad breath, yellow or brown tartar buildup, red or swollen gums, difficulty eating, pawing at the face, loose or missing teeth, and excessive drooling. However, many pets hide dental pain well, so regular professional cleanings are important even if your pet seems fine.',
    category: 'Symptoms',
  },
  {
    question: 'How can I care for my pet\'s teeth at home?',
    answer: "Daily brushing with pet-safe toothpaste is ideal, but even 2-3 times per week helps. Dental chews, water additives, and special dental diets can also help. However, home care cannot replace professional cleanings, as only anesthesia allows us to clean below the gumline where disease starts.",
    category: 'Home Care',
  },
  {
    question: 'Is it normal for pets to lose teeth?',
    answer: "While some tooth loss can occur with age, it's not inevitable. Most tooth loss is due to preventable dental disease. With proper professional dental care and home maintenance, many pets can keep most of their teeth throughout their lives.",
    category: 'Education',
  },
  {
    question: 'What causes tooth loss in pets?',
    answer: 'The primary cause is periodontal disease - infection and inflammation of the gums and supporting structures. This starts with plaque buildup that hardens into tartar, leading to gum recession, bone loss, and eventually tooth loss. Professional cleanings prevent this progression.',
    category: 'Education',
  },
  {
    question: 'Do you work with my regular vet?',
    answer: "Absolutely! We're partners in your pet's health and share all records. We work alongside your regular veterinarian, not against them. We handle the specialized dental care while they continue to provide your pet's general health needs.",
    category: 'Process',
  },
  {
    question: 'What if my pet has other health conditions?',
    answer: "We perform pre-procedure bloodwork and health assessments to ensure your pet is a good candidate for anesthesia. For pets with health conditions, we work closely with your regular vet to develop a safe treatment plan that considers their overall health status.",
    category: 'Safety',
  },
  {
    question: 'How long does the procedure take?',
    answer: 'Procedure time depends on your pet’s health and the treatment needed. Ask the team about the expected procedure, recovery, and pickup timing for your pet.',
    category: 'Process',
  },
  {
    question: 'Do you offer payment plans?',
    answer:
      'Yes. Our primary financing partner is Cherry — apply at pay.withcherry.com/chomp-pet-dental (no hard credit check). We also work with other approved financing partners when needed. Cost should not be a barrier to your pet’s health.',
    category: 'Pricing',
  },
  {
    question: 'Can I save on a second dental cleaning at CHOMP?',
    answer:
      'Yes. For proactive pet parents who want to prevent dental disease, save $150 on your pet’s second dental cleaning when you prepay and schedule it within 12 months of the first.',
    category: 'Pricing',
  },
  {
    question: 'How much does pet dental cleaning cost in Fort Lauderdale?',
    answer:
      'We publish totally transparent, itemized pricing: doctor exam complimentary when you proceed with CHOMP-recommended dental care ($50 if you do not proceed), bloodwork $150 (CBC/chemistry 10 panel), dental cleaning $495 for cats and $595 for dogs (includes anesthesia/sedation, dental X-rays, scaling/charting/polishing), extractions $100–$200 by tooth type, Periovive $75, post-op medications $15–$30 if required. For proactive pet parents, save $150 on the second dental cleaning when you prepay and schedule it within 12 months of the first.',
    category: 'Pricing',
  },
];

// ============================================================================
// PAGE METADATA
// ============================================================================

export const pagesMeta: Record<string, PageMeta> = {
  home: {
    title: 'CHOMP Pet Dental | Dog & Cat Dental Care in Fort Lauderdale',
    description:
      'South Florida pet dental care with totally transparent pricing: cleaning from $495 (cat) / $595 (dog), complimentary doctor exam when you proceed with recommended care, and Cherry payment plans. Book today.',
    canonical: 'https://chomp.vet/',
    keywords: ['pet dentist Fort Lauderdale', 'dog dentist Miami', 'cat teeth cleaning', 'veterinary dental care'],
    ogImage: 'https://chomp.vet/homeimagebouviercat.png',
  },
  services: {
    title: 'Pet Dental Services | Dog & Cat Teeth Cleaning in Fort Lauderdale | CHOMP',
    description:
      'Totally transparent dental pricing: cleaning $495/$595 (cat/dog), itemized rates, $150 off a prepaid second cleaning, and Cherry financing. Serving Miami, Boca Raton, and South Florida.',
    canonical: 'https://chomp.vet/services',
    keywords: ['pet dental services', 'dog teeth cleaning', 'cat dental care', 'veterinary dentistry'],
  },
  research: {
    title: 'Pet Dental Research | Benefits of Veterinary Dental Care | CHOMP',
    description: 'Read veterinary guidance from AAHA and Cornell on dental care, anesthesia, and feline dental disease. Prepare questions for your pet’s visit.',
    canonical: 'https://chomp.vet/research',
  },
  team: {
    title: 'Our Veterinary Team | CHOMP Pet Dental, Fort Lauderdale',
    description: 'Meet the veterinary team at CHOMP Pet Dental in Fort Lauderdale. Our clinic focuses on dental care for dogs and cats.',
    canonical: 'https://chomp.vet/team',
  },
  contact: {
    title: 'Contact CHOMP Pet Dental | Fort Lauderdale Clinic',
    description: 'Contact CHOMP Pet Dental to schedule dog & cat dental cleanings and treatments in South Florida. We\'re here to help with your pet\'s dental health.',
    canonical: 'https://chomp.vet/contact',
  },
  book: {
    title: 'Book Pet Dental Cleaning | Dog & Cat Dentist in Fort Lauderdale | CHOMP',
    description: 'Request dog or cat dental care at CHOMP in Fort Lauderdale through our booking portal, or call (954) 466-3775. Our team will confirm appointment details.',
    canonical: 'https://chomp.vet/book',
  },
  blog: {
    title: 'Pet Dental Health Blog | Tips & Advice | CHOMP Pet Dental',
    description: 'Expert pet dental health advice from Fort Lauderdale veterinarians. Learn about dog and cat dental care, costs, safety, and prevention.',
    canonical: 'https://chomp.vet/blog',
  },
  referrals: {
    title: 'Refer a Case | CHOMP Pet Dental | For Veterinarians & Pet Professionals',
    description: 'Refer a patient to CHOMP Pet Dental. South Florida\'s first clinic 100% dedicated to veterinary dentistry. Fast communication, transparent pricing, same-day reports.',
    canonical: 'https://chomp.vet/referrals',
  },
  southFlorida: {
    title: 'South Florida Pet Dental Care | Dog & Cat Dentist | CHOMP',
    description: 'Dog and cat dental care for South Florida residents at our Fort Lauderdale clinic. View the communities we serve, services, pricing, and appointment information.',
    canonical: 'https://chomp.vet/south-florida-pet-dental',
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get location data by ID/slug
 */
export const getLocationById = (id: string): LocationData | undefined => {
  return locations.find(loc => loc.id === id || loc.slug === id);
};

/**
 * Get service data by ID/slug
 */
export const getServiceById = (id: string): ServiceData | undefined => {
  return services.find(svc => svc.id === id || svc.slug === id);
};

/**
 * Get blog post by slug
 */
export const getBlogPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find(post => post.slug === slug);
};

/**
 * Get all location slugs for sitemap generation
 */
export const getAllLocationSlugs = (): string[] => {
  return locations.map(loc => loc.slug);
};

/**
 * Get all blog slugs for sitemap generation
 */
export const getAllBlogSlugs = (): string[] => {
  return blogPosts.map(post => post.slug);
};

/**
 * Get all service slugs for sitemap generation
 */
export const getAllServiceSlugs = (): string[] => {
  return services.map(svc => svc.slug);
};

/**
 * Get featured blog posts
 */
export const getFeaturedPosts = (): BlogPost[] => {
  return blogPosts.filter(post => post.featured);
};

/**
 * Get posts by category
 */
export const getPostsByCategory = (category: string): BlogPost[] => {
  return blogPosts.filter(post => post.category === category);
};

/**
 * Get FAQs by category
 */
export const getFAQsByCategory = (category: string): FAQItem[] => {
  return faqItems.filter(faq => faq.category === category);
};



